/**
 * The full story of one lead: message, qualifying answers, contact, delivery
 * outcome, provenance. Shared by every screen that lists leads — Leads, Held,
 * Deliveries — so "expand a row" means the same thing everywhere, and a held
 * lead is judged on exactly the information the partner would have received.
 */
import { siteName, type LeadWithDeliveries } from "../lib/data.ts";
import { absoluteTime, relativeTime } from "../lib/format.ts";
import { Badge, type Tone } from "./ui.tsx";
import { DETAIL_LABELS } from "@pipeline/core/vocabulary.ts";
import type { DeliveryRecord } from "@pipeline/types.ts";

/**
 * The label `core/vocabulary.ts` gives the `intent` column.
 *
 * `intent` — buy / lease / rent — is the single most sales-relevant answer on
 * the form and appears on roughly 70% of leads, so callers pull it up onto the
 * collapsed row rather than leaving it in the detail panel with everything
 * else. Reading the label from the vocabulary means it cannot drift from what
 * the partner sees in their email.
 */
export const INTENT_LABEL = DETAIL_LABELS.intent ?? "Looking to";

/** The worst thing that happened to this lead's deliveries, which is what a
 *  human scanning the list needs to see. */
export function deliveryOutcome(
  deliveries: Array<{ delivery: DeliveryRecord }>,
): { tone: Tone; label: string } | null {
  if (deliveries.length === 0) return null;
  const statuses = new Set(deliveries.map((entry) => entry.delivery.status));

  if (statuses.has("dead")) return { tone: "bad", label: "gave up" };
  if (statuses.has("bounced")) return { tone: "bad", label: "bounced" };
  if (statuses.has("complained")) return { tone: "warn", label: "marked spam" };
  if (statuses.has("pending") || statuses.has("sending")) return { tone: "neutral", label: "queued" };
  if (statuses.has("delivered")) return { tone: "ok", label: "delivered" };
  return { tone: "ok", label: "sent" };
}

function deliveryLine(delivery: DeliveryRecord): string {
  switch (delivery.status) {
    case "delivered":
      return `Accepted by their mail server ${relativeTime(delivery.deliveredAt || delivery.sentAt)}`;
    case "sent":
      return `Handed to the mail provider ${relativeTime(delivery.sentAt)}`;
    case "pending":
    case "sending":
      return `Queued, ${delivery.attempts} ${delivery.attempts === 1 ? "attempt" : "attempts"} so far`;
    case "bounced":
      return "Permanently rejected by their mail server";
    case "complained":
      return "Delivered, then reported as spam";
    case "dead":
      return `Gave up after ${delivery.attempts} attempts`;
    default:
      return delivery.status;
  }
}

export function LeadDetail({ entry }: { entry: LeadWithDeliveries }) {
  const { lead, deliveries } = entry;

  return (
    <div className="lead-detail">
      <div className="stack stack--4">
        <div>
          <p className="lead-detail__label">Their message</p>
          {lead.message ? (
            <p className="lead-detail__message">{lead.message}</p>
          ) : (
            <p className="muted" style={{ fontSize: "var(--t-sm)" }}>
              They left the message field empty.
            </p>
          )}
        </div>

        <div>
          <p className="lead-detail__label">Qualifying answers</p>
          {lead.details.length === 0 ? (
            <p className="muted" style={{ fontSize: "var(--t-sm)" }}>
              This form asked no qualifying questions.
            </p>
          ) : (
            <dl className="answers">
              {lead.details.map((detail) => (
                <div key={`${detail.label}:${detail.value}`} style={{ display: "contents" }}>
                  <dt className="answers__key">{detail.label}</dt>
                  <dd className="answers__value" data-lead={detail.label === INTENT_LABEL}>
                    {detail.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>

        {lead.qualityReasons.length > 0 && lead.quality !== "clean" && (
          <div>
            <p className="lead-detail__label">Why it was flagged</p>
            <div className="tags">
              {lead.qualityReasons.map((reason) => (
                <span className="tag" key={reason}>
                  <span className="tag__value">{reason}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="stack stack--4">
        <div>
          <p className="lead-detail__label">Contact</p>
          <div className="record__contact">
            {lead.email ? (
              <a href={`mailto:${lead.email}`}>{lead.email}</a>
            ) : (
              <span className="dim">no email</span>
            )}
            {lead.phone ? (
              <a href={`tel:${lead.phone}`}>{lead.phone}</a>
            ) : (
              <span className="dim">no phone</span>
            )}
            {lead.city && <span>{lead.city}</span>}
          </div>
        </div>

        <div>
          <p className="lead-detail__label">Delivery</p>
          {deliveries.length === 0 ? (
            <p className="muted" style={{ fontSize: "var(--t-sm)" }}>
              {lead.routingStatus === "held"
                ? "Held for review, so nothing has been queued."
                : lead.routingStatus === "skipped"
                  ? "Skipped, so nothing was queued."
                  : "Not queued yet."}
            </p>
          ) : (
            <div className="stack" style={{ gap: "var(--sp-2)" }}>
              {deliveries.map(({ delivery, destination }) => (
                <div key={delivery.id} className="stack" style={{ gap: 3 }}>
                  <div className="row row--2">
                    <span style={{ fontSize: "var(--t-sm)", fontWeight: 520 }}>
                      {destination?.name ?? "Unknown destination"}
                    </span>
                    <Badge tone={deliveryOutcome([{ delivery }])?.tone ?? "neutral"}>
                      {delivery.status}
                    </Badge>
                  </div>
                  <span className="lead-detail__meta">{deliveryLine(delivery)}</span>
                  {delivery.lastError && (
                    <span className="lead-detail__meta" style={{ color: "var(--bad)" }}>
                      {delivery.lastError}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="lead-detail__label">Where it came from</p>
          <dl className="lead-detail__meta stack" style={{ gap: 3 }}>
            <div className="row row--2">
              <dt>Site</dt>
              <dd>{siteName(lead.siteKey)}</dd>
            </div>
            <div className="row row--2">
              <dt>Submitted</dt>
              <dd className="mono">{absoluteTime(lead.submittedAt)}</dd>
            </div>
            {lead.sourceUrl && (
              <div className="row row--2">
                <dt>Page</dt>
                <dd>{lead.sourceUrl}</dd>
              </div>
            )}
            {Object.entries(lead.utm).map(([key, value]) => (
              <div className="row row--2" key={key}>
                <dt>{key}</dt>
                <dd>{value}</dd>
              </div>
            ))}
            <div className="row row--2">
              <dt>Source key</dt>
              <dd className="mono">{lead.sourceKey}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
}
